import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/react';
import { Link, useOutletContext } from 'react-router-dom';
import { resolveTestedAt } from './SettingsState';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.horizontl.io';
const REQUEST_TIMEOUT_MS = 15000;
const AI_PREVIEW_TIMEOUT_MS = 180000;

const withTimeout = (promise, message) => new Promise((resolve, reject) => {
  const timeout = window.setTimeout(() => reject(new Error(message)), REQUEST_TIMEOUT_MS);
  Promise.resolve(promise).then(
    value => {
      window.clearTimeout(timeout);
      resolve(value);
    },
    reason => {
      window.clearTimeout(timeout);
      reject(reason);
    },
  );
});

const errorMessage = detail => ({
  direct_response_acknowledgement_required: 'Acknowledge the customer-visible delivery warning before saving.',
  configuration_draft_changed: 'This draft changed in another session. Reload it before continuing.',
  configuration_test_required: 'Run and confirm a successful test for this exact draft before publishing.',
  settings_test_ticket_not_selected: 'Choose a ticket from one of the projects selected in this draft.',
  settings_test_ticket_priority_excluded: 'This ticket priority is excluded by the draft configuration.',
  settings_test_ticket_not_accessible: 'Horizontl could not access that ticket. Check its key and Jira permissions.',
  settings_test_public_delivery_failed: 'Jira did not confirm the public test response. Check the connected account’s comment permission before trying a different test ticket.',
  settings_test_delivery_requires_review: 'The public delivery result is uncertain. Check the Jira ticket before running another test; Horizontl will not risk posting a duplicate comment.',
  settings_test_preview_expired: 'This test preview expired after 60 minutes. Generate a new preview before confirming it.',
  configuration_options_unavailable: 'Connected project or knowledge-source options could not be loaded.',
  configuration_version_required: 'The saved configuration selected for this test is no longer available. Reload Settings and try again.',
  single_project_required: 'Select one Jira project for this configuration.',
  workspace_activation_required: 'Complete workspace activation before resuming Dana.',
  dana_automation_globally_disabled: 'Dana is temporarily unavailable across Horizontl. Live processing, AI tests and Jira delivery are disabled until service is restored.',
}[detail] || detail || 'The request could not be completed.');

const responseModeLabel = mode => (
  mode === 'direct_response'
    ? 'Direct response · customer-visible autonomous delivery'
    : 'Draft for review · human approval required'
);

const displayPriority = (value, options) => {
  if (value === '*') return 'All available priorities';
  return options.find(priority => priority.key === value)?.name || value;
};

const CheckItem = ({ children, detail }) => (
  <div className="configuration-check">
    <span aria-hidden="true">✓</span>
    <div><strong>{children}</strong>{detail && <small>{detail}</small>}</div>
  </div>
);

const StepHeading = ({ number, title, optional, children }) => (
  <div className="step-heading">
    <span className="step-number" aria-hidden="true">{number}</span>
    <div>
      <div className="step-title-row">
        <h2>{title}</h2>
        {optional && <span className="optional-chip">Optional</span>}
      </div>
      {children && <p>{children}</p>}
    </div>
  </div>
);

function LiveConfiguration({ data, priorityOptions, busy, onToggleAutomation }) {
  const configuration = data.live.configuration;
  const automationEnabled = data.live.automation_enabled;
  const platformEnabled = data.live.platform_automation_enabled !== false;
  const effectiveAutomationEnabled = automationEnabled && platformEnabled;
  const settingsUpdatedAt = data.live.settings_updated_at || data.live.published_at;
  return (
    <div className="configuration-tab-content">
      <div className={`automation-status-card ${effectiveAutomationEnabled ? 'active' : 'paused'}`}>
        <div>
          <span className="automation-eyebrow">Live automation</span>
          <strong>{!platformEnabled ? 'Dana is temporarily unavailable' : automationEnabled ? 'Dana is active' : 'Dana is paused'}</strong>
          <p>{!platformEnabled
            ? 'Horizontl has temporarily stopped Dana across the platform. New Jira tickets are not processed or queued.'
            : automationEnabled
            ? 'New matching Jira tickets are processed using the published settings below.'
            : 'New Jira tickets are not processed or queued while Dana is paused.'}</p>
          {data.live.automation_status_updated_at && (
            <small>Status changed {new Date(data.live.automation_status_updated_at).toLocaleString('en-GB')}.</small>
          )}
        </div>
        <button
          type="button"
          className={automationEnabled ? 'danger-button' : 'primary-button'}
          disabled={!data.editable || busy === 'automation' || (!platformEnabled && !automationEnabled)}
          onClick={() => onToggleAutomation(!automationEnabled)}
        >
          {busy === 'automation'
            ? 'Updating…'
            : automationEnabled ? 'Pause Dana' : 'Resume Dana'}
        </button>
      </div>
      {!automationEnabled && (
        <div className="pause-warning">
          Tickets created while Dana is paused will not be processed automatically later. You can still test an individual ticket manually. Resuming applies only to tickets created afterwards.
        </div>
      )}
      <section className="settings-grid">
        <article className="panel configuration-summary">
          <div className="section-label">Ticketing system</div>
          <CheckItem detail={data.connection.site_url}>Jira connected</CheckItem>
          {configuration.projects.map(project => (
            <CheckItem key={project.id}>{project.name} ({project.key})</CheckItem>
          ))}
        </article>
        <article className="panel configuration-summary">
          <div className="section-label">Knowledge sources</div>
          {configuration.knowledge_sources.length ? configuration.knowledge_sources.map(source => (
            <CheckItem key={source.id}>{source.name}{source.key ? ` (${source.key})` : ''}</CheckItem>
          )) : <CheckItem>No knowledge sources selected</CheckItem>}
        </article>
        <article className="panel configuration-summary">
          <div className="section-label">Ticket priorities</div>
          {configuration.priorities.map(priority => (
            <CheckItem key={priority}>{displayPriority(priority, priorityOptions)}</CheckItem>
          ))}
        </article>
        <article className="panel configuration-summary">
          <div className="section-label">Response behaviour</div>
          <CheckItem>{responseModeLabel(configuration.response_mode)}</CheckItem>
          <CheckItem>Response tone: {configuration.response_tone || 'professional'}</CheckItem>
        </article>
      </section>
      <div className="configuration-footer-note">
        {settingsUpdatedAt
          ? `Live settings last updated ${new Date(settingsUpdatedAt).toLocaleString('en-GB')}.`
          : 'Current workspace configuration.'}
        {' '}Editing or testing a draft does not change these live settings.
      </div>
    </div>
  );
}

export default function Settings() {
  const { getToken } = useAuth();
  const { refresh } = useOutletContext();
  const [activeTab, setActiveTab] = useState(() => (
    new URLSearchParams(window.location.search).get('tab') === 'draft' ? 'draft' : 'live'
  ));
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [ticketKey, setTicketKey] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [publishError, setPublishError] = useState('');
  const [publicTestAcknowledged, setPublicTestAcknowledged] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [priorityScope, setPriorityScope] = useState('site');
  const [prioritySyncedAt, setPrioritySyncedAt] = useState(null);
  const [priorityStale, setPriorityStale] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const request = useCallback(async (path, options = {}, timeoutMs = REQUEST_TIMEOUT_MS) => {
    const token = await withTimeout(
      getToken(),
      'Your session could not be loaded. Refresh the page or sign in again.',
    );
    if (!token) throw new Error('Your session has expired. Refresh the page or sign in again.');

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(`${API_URL}${path}`, {
        ...options,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(options.body ? { 'Content-Type': 'application/json' } : {}),
          ...options.headers,
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(errorMessage(payload.detail));
      return payload;
    } catch (caught) {
      if (caught.name === 'AbortError') {
        throw new Error(
          timeoutMs === AI_PREVIEW_TIMEOUT_MS
            ? 'Dana took longer than three minutes to generate this preview. Check the Render logs before retrying.'
            : 'Configuration took too long to load. Check the API deployment and try again.'
        );
      }
      throw caught;
    } finally {
      window.clearTimeout(timeout);
    }
  }, [getToken]);

  const load = useCallback(async () => {
    setBusy('loading');
    setInitialLoadComplete(false);
    setError('');
    try {
      const configuration = await request('/api/settings/configuration');
      const editable = configuration.draft?.configuration || configuration.live.configuration;
      const requiresSingleProjectResave = editable.projects.length !== 1;
      const selectedProjects = requiresSingleProjectResave ? [] : editable.projects;
      setData(configuration);
      setDraft(configuration.draft);
      setPriorityOptions(configuration.options.priorities || []);
      setPriorityScope(configuration.options.priority_catalog_scope || 'site');
      setPrioritySyncedAt(configuration.options.priority_catalog_synced_at || null);
      setPriorityStale(Boolean(configuration.options.priority_catalog_stale));
      setForm({
        project_ids: selectedProjects.map(item => item.id),
        knowledge_source_ids: editable.knowledge_sources.map(item => item.id),
        priorities: requiresSingleProjectResave ? [] : editable.priorities,
        response_mode: editable.response_mode,
        response_tone: editable.response_tone,
        direct_response_acknowledged: Boolean(editable.direct_response_acknowledged),
      });
      setDirty(requiresSingleProjectResave);
      setPreview(null);
      setPublicTestAcknowledged(false);
      setTestResult(null);
      if (requiresSingleProjectResave) {
        setNotice('Choose one Jira project and save this configuration before testing.');
        setActiveTab('draft');
      } else if (new URLSearchParams(window.location.search).get('published') === '1') {
        setNotice('The tested configuration is now live.');
        setActiveTab('live');
      }
    } catch (caught) {
      setError(caught.message);
    } finally {
      setBusy('');
      setInitialLoadComplete(true);
    }
  }, [request]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!dirty) return undefined;
    const protectUnsavedDraft = event => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', protectUnsavedDraft);
    return () => window.removeEventListener('beforeunload', protectUnsavedDraft);
  }, [dirty]);

  const selectTab = tab => {
    setActiveTab(tab);
    const url = new URL(window.location.href);
    if (tab === 'draft') url.searchParams.set('tab', 'draft');
    else url.searchParams.delete('tab');
    url.searchParams.delete('published');
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  };

  const update = changes => {
    setForm(current => ({ ...current, ...changes }));
    setDirty(true);
    setPreview(null);
    setPublicTestAcknowledged(false);
    setTestResult(null);
    setNotice('');
  };

  const toggle = (field, id) => {
    const values = form[field];
    update({ [field]: values.includes(id) ? values.filter(value => value !== id) : [...values, id] });
  };

  const applyPriorityCatalog = result => {
    const options = result.priorities || [];
    setPriorityOptions(options);
    setPriorityScope(result.scope || 'site');
    setPrioritySyncedAt(result.synced_at || null);
    setPriorityStale(Boolean(result.is_stale));
    const valid = new Set(options.map(item => item.key));
    const priorities = form.priorities.includes('*')
      ? ['*']
      : form.priorities.filter(value => valid.has(value));
    if (priorities.join(',') !== form.priorities.join(',')) update({ priorities });
  };

  const selectProject = async projectId => {
    const projectIds = [projectId];
    update({ project_ids: projectIds });
    if (!projectIds.length) {
      setPriorityOptions([]);
      return;
    }
    setBusy('priorities');
    setError('');
    try {
      const result = await request('/api/settings/priorities/options', {
        method: 'POST',
        body: JSON.stringify({ project_ids: projectIds }),
      });
      applyPriorityCatalog(result);
    } catch (caught) {
      setError(caught.message);
    } finally {
      setBusy('');
    }
  };

  const togglePriority = priority => {
    if (priority === '*') {
      update({ priorities: form.priorities.includes('*') ? [] : ['*'] });
      return;
    }
    const selected = form.priorities.includes('*') ? [] : form.priorities;
    update({
      priorities: selected.includes(priority)
        ? selected.filter(value => value !== priority)
        : [...selected, priority],
    });
  };

  const refreshPriorities = async () => {
    if (!form.project_ids.length) return;
    setBusy('priorities');
    setError('');
    setNotice('');
    try {
      const result = await request('/api/settings/priorities/options', {
        method: 'POST',
        body: JSON.stringify({ project_ids: form.project_ids }),
      });
      applyPriorityCatalog(result);
      setNotice('Priority names refreshed from Jira. Live processing has not changed.');
    } catch (caught) {
      setError(caught.message);
    } finally {
      setBusy('');
    }
  };

  const saveDraft = async () => {
    setBusy('save');
    setError('');
    setNotice('');
    try {
      const response = await request('/api/settings/configuration/draft', {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      setDraft({
        ...response.draft,
        status: 'draft',
        configuration: {
          projects: data.options.projects.filter(item => form.project_ids.includes(item.id)),
          knowledge_sources: data.options.knowledge_sources.filter(item => form.knowledge_source_ids.includes(item.id)),
          priorities: form.priorities,
          response_mode: form.response_mode,
          response_tone: form.response_tone,
          direct_response_acknowledged: form.direct_response_acknowledged,
        },
      });
      setDirty(false);
      setPreview(null);
      setTestResult(null);
      setNotice('Draft saved. You can publish it now or run an optional ticket test.');
    } catch (caught) {
      setError(caught.message);
    } finally {
      setBusy('');
    }
  };

  const runTest = async () => {
    setBusy('test');
    setError('');
    setNotice('');
    setPreview(null);
    setPreviewError('');
    setPublicTestAcknowledged(false);
    try {
      const response = await request('/api/settings/configuration/test/preview', {
        method: 'POST',
        body: JSON.stringify({
          version_id: testVersion.id,
          revision: testVersion.revision,
          ticket_key: ticketKey,
        }),
      }, AI_PREVIEW_TIMEOUT_MS);
      setPreview(response);
      setNotice(response.delivery === 'public_on_confirmation'
        ? 'Preview generated. Nothing has been posted yet; review it before confirming the public response.'
        : 'Test response generated and saved in Horizontl. Nothing was posted externally.');
    } catch (caught) {
      setPreviewError(caught.message);
    } finally {
      setBusy('');
    }
  };

  const confirmTest = async () => {
    setBusy('confirm');
    setError('');
    setPreviewError('');
    try {
      const response = await request('/api/settings/configuration/test/confirm', {
        method: 'POST',
        body: JSON.stringify({ preview_id: preview.preview_id }),
      });
      // Confirmation is persisted by the API. Reload that server state so the
      // publish gate never depends on a temporary client-side state update.
      await load();
      // Refresh the shared ticket collection before linking to the newly
      // created controlled-test row. Without this, Tickets can display a
      // previous run with the same Jira key from the cached layout state.
      await refresh({ silent: true });
      setActiveTab('draft');
      setTestResult(response);
      setNotice((response.delivery?.status === 'sent'
        ? 'Test passed and Dana’s response was posted publicly to the Jira ticket.'
        : 'Draft test passed in Horizontl.'));
    } catch (caught) {
      setPreviewError(caught.message);
    } finally {
      setBusy('');
    }
  };

  const publish = async () => {
    setBusy('publish');
    setError('');
    setPublishError('');
    try {
      await request('/api/settings/configuration/publish', {
        method: 'POST',
        body: JSON.stringify({ version_id: draft.id, revision: draft.revision }),
      });
      window.location.assign('/settings?published=1');
    } catch (caught) {
      setPublishError(caught.message);
    } finally {
      setBusy('');
    }
  };

  const toggleAutomation = async enabled => {
    const confirmed = window.confirm(enabled
      ? 'Resume Dana? Eligible Jira tickets created after Dana resumes will be processed using the current live settings. Tickets received while paused remain unprocessed.'
      : 'Pause Dana? New tickets will not be processed or queued. Already-running responses may still finish.');
    if (!confirmed) return;
    setBusy('automation');
    setError('');
    setNotice('');
    try {
      const response = await request('/api/settings/configuration/automation', {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
      });
      setData(current => ({
        ...current,
        live: {
          ...current.live,
          automation_enabled: response.enabled,
          automation_status_updated_at: response.updated_at,
        },
      }));
      setNotice(response.enabled
        ? 'Dana resumed. New matching Jira tickets will use the current live settings. Tickets received while paused remain unprocessed.'
        : 'Dana paused. New Jira tickets will not be processed or queued.');
    } catch (caught) {
      setError(caught.message);
    } finally {
      setBusy('');
    }
  };

  const skipOptionalTest = () => {
    document.getElementById('publish-settings')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setNotice('Optional testing skipped. Review the saved draft before publishing it live.');
  };

  const reconnect = async provider => {
    setBusy(`connect-${provider}`);
    setError('');
    try {
      const response = await request(`/api/settings/${provider}/authorize`, { method: 'POST' });
      if (!response.authorization_url) throw new Error(`Could not start ${provider} authorization.`);
      window.location.assign(response.authorization_url);
    } catch (caught) {
      setError(caught.message);
      setBusy('');
    }
  };

  const canSave = useMemo(() => (
    data?.editable
    && !busy
    && form?.project_ids.length
    && form?.priorities.length
    && (form.response_mode !== 'direct_response' || form.direct_response_acknowledged)
    && (dirty || !draft)
  ), [busy, data?.editable, dirty, draft, form]);

  const testVersion = !dirty ? (draft || data?.live) : null;
  const testingLiveConfiguration = testVersion?.status === 'published';
  const canTest = Boolean(testVersion && !busy && ticketKey.trim());
  const canPublish = Boolean(
    data?.editable && draft && !dirty && !busy
  );
  const testTicketUrl = testResult?.ticket_url || (
    draft?.tested_ticket_id
      ? `/tickets?highlight=${encodeURIComponent(draft.tested_ticket_id)}&settings_test=1`
      : null
  );
  const testedAt = resolveTestedAt(testResult, draft);

  if (busy === 'loading' || !initialLoadComplete) {
    return <div className="panel">Loading configuration…</div>;
  }

  if (!form || !data) {
    return (
      <div className="page-stack configuration-page">
        <div className="error-banner">{error || 'Configuration could not be loaded.'}</div>
        <div>
          <button type="button" className="primary-button" onClick={load}>Retry configuration</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-stack configuration-page">
      <header className="configuration-header">
        <div>
          <h2>Configuration</h2>
          <p>Review what is live or safely prepare, test and publish a replacement.</p>
        </div>
        <div className="configuration-tabs" role="tablist" aria-label="Configuration versions">
          <button type="button" role="tab" aria-selected={activeTab === 'live'} className={activeTab === 'live' ? 'active' : ''} onClick={() => selectTab('live')}>Live</button>
          <button type="button" role="tab" aria-selected={activeTab === 'draft'} className={activeTab === 'draft' ? 'active' : ''} onClick={() => selectTab('draft')}>Draft &amp; test{dirty ? ' •' : ''}</button>
        </div>
      </header>

      {error && <div className="error-banner">{error}</div>}
      {notice && <div className="success-banner"><span>{notice}</span></div>}

      {activeTab === 'live' ? (
        <LiveConfiguration
          data={data}
          priorityOptions={priorityOptions}
          busy={busy}
          onToggleAutomation={toggleAutomation}
        />
      ) : (
        <div className="configuration-tab-content">
          <div className="draft-notice">
            <strong>{dirty ? 'Unsaved draft changes' : draft ? `Editing draft v${draft.version_number}.${draft.revision}` : 'Create a test draft'}</strong>
            <span>
              {draft?.updated_at ? `Draft settings last updated ${new Date(draft.updated_at).toLocaleString('en-GB')}. ` : ''}
              Nothing here affects live processing until this saved version is published.
            </span>
          </div>

          <div className={`draft-live-status ${data.live.automation_enabled && data.live.platform_automation_enabled !== false ? 'active' : 'paused'}`}>
            <div>
              <strong>{data.live.platform_automation_enabled === false ? 'Dana is temporarily unavailable' : data.live.automation_enabled ? 'Dana is currently active' : 'Dana is currently paused'}</strong>
              <span>{data.live.platform_automation_enabled === false
                ? 'Horizontl has temporarily stopped Dana across the platform. Draft settings remain available, but AI tests and live processing are disabled.'
                : data.live.automation_enabled
                ? 'New Jira tickets will still be processed using the live settings while you edit this draft. Pause Dana before creating a clean public test ticket.'
                : 'New Jira tickets are not processed or queued, so you can create and test a ticket cleanly. Resuming applies only to tickets created afterwards.'}</span>
            </div>
            <button type="button" className="secondary-button" onClick={() => selectTab('live')}>
              {data.live.platform_automation_enabled !== false && data.live.automation_enabled ? 'Go to Live to pause Dana' : 'View live controls'}
            </button>
          </div>

          <section className="panel settings-step">
            <StepHeading number="1" title="Confirm connections">Check the systems Dana can use before changing behaviour.</StepHeading>
            <div className="panel-heading">
              <div><div className="section-label">Connected systems</div><p>{data.connection.site_url} · {data.connection.status}</p></div>
              <div className="button-row">
                <button className="secondary-button" disabled={!data.editable || busy} onClick={() => reconnect('jira')}>{busy === 'connect-jira' ? 'Opening…' : 'Reconnect Jira'}</button>
                <button className="secondary-button" disabled={!data.editable || busy} onClick={() => reconnect('confluence')}>{busy === 'connect-confluence' ? 'Opening…' : data.connection.knowledge_connected ? 'Reconnect Confluence' : 'Connect Confluence'}</button>
              </div>
            </div>
          </section>

          <section className="settings-step">
            <StepHeading number="2" title="Choose settings">Select what Dana monitors and how responses should be prepared.</StepHeading>
            <div className="settings-grid">
            <article className="panel form-section">
              <div className="section-label">Project and board</div>
              <p>Select the single Jira project Dana should monitor.</p>
              <div className="check-list">
                {data.options.projects.map(project => (
                  <label key={project.id}>
                    <input type="radio" name="jira-project" checked={form.project_ids[0] === project.id} disabled={!data.editable || busy === 'priorities'} onChange={() => selectProject(project.id)} />
                    <span>{project.name} ({project.key})</span>
                  </label>
                ))}
              </div>
            </article>

            <article className="panel form-section">
              <div className="section-label">Knowledge sources</div>
              <div className="check-list">
                {data.options.knowledge_sources.map(source => (
                  <label key={source.id}>
                    <input type="checkbox" checked={form.knowledge_source_ids.includes(source.id)} disabled={!data.editable} onChange={() => toggle('knowledge_source_ids', source.id)} />
                    <span>{source.name}{source.key ? ` (${source.key})` : ''}</span>
                  </label>
                ))}
                {!data.options.knowledge_sources.length && <span className="compact-empty-state">No connected sources available.</span>}
              </div>
            </article>

            <article className="panel form-section">
              <div className="panel-heading">
                <div><div className="section-label">Ticket priorities</div><p>Select which existing priorities Dana should process.</p></div>
                <button type="button" className="secondary-button" disabled={!data.editable || busy || !form.project_ids.length} onClick={refreshPriorities}>{busy === 'priorities' ? 'Syncing…' : 'Sync from Jira'}</button>
              </div>
              <div className="check-list priority-grid">
                <label>
                  <input type="checkbox" checked={form.priorities.includes('*')} disabled={!data.editable || !priorityOptions.length} onChange={() => togglePriority('*')} />
                  <span>All available priorities</span>
                </label>
                {priorityOptions.map(priority => (
                  <label key={priority.key}>
                    <input type="checkbox" checked={form.priorities.includes('*') || form.priorities.includes(priority.key)} disabled={!data.editable || form.priorities.includes('*')} onChange={() => togglePriority(priority.key)} />
                    <span>{priority.name}</span>
                  </label>
                ))}
              </div>
              <span className="field-help">
                {priorityScope === 'projects' ? 'Priorities available to the selected Jira projects.' : 'Priorities available across this Jira site.'}
                {prioritySyncedAt ? ` Last synced ${new Date(prioritySyncedAt).toLocaleString('en-GB')}.` : ''}
                {priorityStale ? ' Refresh recommended.' : ''}
                {' '}Changing the selection uses the saved list and does not contact Jira.
              </span>
            </article>

            <article className="panel form-section">
              <div className="section-label">Response behaviour</div>
              <label className={`choice-card ${form.response_mode === 'draft_only' ? 'selected' : ''}`}>
                <input type="radio" name="mode" checked={form.response_mode === 'draft_only'} disabled={!data.editable} onChange={() => update({ response_mode: 'draft_only', direct_response_acknowledged: false })} />
                <div><strong>Draft for review</strong><span>Human approval is required. Responses remain in Horizontl.</span></div>
              </label>
              <label className={`choice-card ${form.response_mode === 'direct_response' ? 'selected' : ''}`}>
                <input type="radio" name="mode" checked={form.response_mode === 'direct_response'} disabled={!data.editable} onChange={() => update({ response_mode: 'direct_response', direct_response_acknowledged: false })} />
                <div><strong>Direct response</strong><span>Customer-visible autonomous response in the ticketing system.</span></div>
              </label>
              {form.response_mode === 'direct_response' && (
                <>
                  <label className="caution">
                    <input type="checkbox" checked={form.direct_response_acknowledged} onChange={event => update({ direct_response_acknowledged: event.target.checked })} />
                    I understand that publishing this mode permits customer-visible responses without human approval.
                  </label>
                  <span className="field-help">Testing is optional. Confirming a Direct response test posts that exact response publicly to the selected Jira ticket.</span>
                </>
              )}
              <div className="form-field">
                <label htmlFor="tone">Response tone</label>
                <select id="tone" value={form.response_tone} disabled={!data.editable} onChange={event => update({ response_tone: event.target.value })}>
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="formal">Formal</option>
                  <option value="technical">Technical</option>
                </select>
              </div>
            </article>
            </div>
          </section>

          <section className="panel settings-step">
            <StepHeading number="3" title="Save the draft">Save the exact settings you want to test or publish. Jira is not contacted.</StepHeading>
            <div className="draft-save-row">
              <button type="button" className="primary-button" onClick={saveDraft} disabled={!canSave}>{busy === 'save' ? 'Saving…' : draft ? 'Save draft changes' : 'Save configuration as draft'}</button>
              <span>{dirty ? 'Save these changes before testing or publishing.' : draft ? `Draft saved${draft.updated_at ? ` ${new Date(draft.updated_at).toLocaleString('en-GB')}` : ''}. Live processing is unchanged.` : 'Create a draft to test or publish it.'}</span>
            </div>
          </section>

          <section className="panel test-panel settings-step">
            <StepHeading number="4" title="Test with a Jira ticket" optional>Preview the saved draft using a real ticket. Testing is not required to publish. Tickets created while Dana is paused will not run automatically after it resumes.</StepHeading>
            <div className="test-controls">
              <input type="text" value={ticketKey} onChange={event => setTicketKey(event.target.value)} placeholder="KAN-25 or Jira URL" />
              <button type="button" className="secondary-button" onClick={runTest} disabled={!canTest}>{busy === 'test' ? 'Generating…' : 'Generate AI test preview'}</button>
            </div>
            {previewError && <div className="error-banner test-error">{previewError}</div>}
            {dirty && <span className="field-help">Save the draft in step 3 before testing so Dana uses these exact settings.</span>}
            {testingLiveConfiguration && <span className="field-help">Testing the current live configuration. Unsaved settings changes are not included.</span>}
            {preview && (
              <div className="test-preview">
                <strong>{preview.ticket.key} — {preview.ticket.summary}</strong>
                <div className="simulation-label">{preview.delivery === 'public_on_confirmation'
                  ? 'Not posted yet · confirming will send this exact response publicly in Jira'
                  : 'Draft for review · nothing will be posted to Jira'}</div>
                {preview.draft.confidence_score != null && <div className="confidence-explanation"><span className={`status-chip ${preview.draft.confidence || ''}`}>{preview.draft.confidence_score}%</span><span>{preview.draft.confidence_explanation}</span></div>}
                <div className="response-copy">{preview.draft.body}</div>
                {preview.delivery === 'public_on_confirmation' && <label className="caution"><input type="checkbox" checked={publicTestAcknowledged} onChange={event => setPublicTestAcknowledged(event.target.checked)} />I confirm this is a test ticket and understand this response will be visible to the customer.</label>}
                <button type="button" className="primary-button" onClick={confirmTest} disabled={busy || (preview.delivery === 'public_on_confirmation' && !publicTestAcknowledged)}>{busy === 'confirm' ? 'Confirming…' : preview.delivery === 'public_on_confirmation' ? 'Post public test response' : 'Confirm draft test'}</button>
              </div>
            )}
            {(testResult || (draft?.test_status === 'passed' && !dirty)) && (
              <div className="success-banner">
                <span>Test passed {testedAt ? new Date(testedAt).toLocaleString('en-GB') : ''}</span>
                {testTicketUrl && <Link to={testTicketUrl}>Review test in Tickets</Link>}
                {testResult?.delivery?.status === 'sent' && testResult.external_ticket_url && <a href={testResult.external_ticket_url} target="_blank" rel="noreferrer">Open response in Jira</a>}
              </div>
            )}
            <div className="step-actions">
              <button type="button" className="text-button" onClick={skipOptionalTest} disabled={busy}>Skip optional test</button>
              <span>Skipping does not change the draft or live automation.</span>
            </div>
          </section>

          <section id="publish-settings" className="panel publish-panel settings-step">
            <StepHeading number="5" title="Publish the saved settings">This atomically replaces the live configuration. Dana’s active or paused state does not change.</StepHeading>
            <button type="button" className="primary-button" onClick={publish} disabled={!canPublish}>{busy === 'publish' ? 'Publishing…' : 'Publish saved configuration'}</button>
            {publishError && <div className="error-banner test-error">{publishError}</div>}
          </section>
        </div>
      )}
    </div>
  );
}
